import { AxiosError, AxiosRequestConfig } from "axios";
import axiosInstance from "./axiosInstance";

export type InvoiceVerificationState =
  | "genuine"
  | "not_genuine"
  | "unavailable";

export interface InvoiceVerificationResult {
  amount?: number | string;
  currency?: string;
  endpoint: string;
  invoiceId: string;
  invoiceNumber?: string;
  message?: string;
  messageKey?: string;
  orderNumber?: string;
  state: InvoiceVerificationState;
}

interface ApiEnvelope {
  data?: unknown;
  message?: unknown;
  success?: unknown;
}

const REQUEST_CONFIG: AxiosRequestConfig & { skipLoader?: boolean } = {
  headers: {
    "Content-Type": "application/json",
  },
  skipLoader: true,
};

const getVerificationEndpoint = (invoiceId: string) =>
  `/invoice/${invoiceId}/genuine`;

const toRecord = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return value as Record<string, unknown>;
};

const readString = (...values: unknown[]): string | undefined => {
  const value = values.find(
    (item) => typeof item === "string" && item.trim().length > 0,
  );

  return typeof value === "string" ? value : undefined;
};

const readAmount = (...values: unknown[]): number | string | undefined => {
  const value = values.find(
    (item) =>
      typeof item === "number" ||
      (typeof item === "string" && item.trim().length > 0),
  );

  if (typeof value === "number" || typeof value === "string") {
    return value;
  }

  return undefined;
};

const includesAny = (value: string, candidates: string[]) =>
  candidates.some((candidate) => value.includes(candidate));

const parseVerificationState = (
  ...values: unknown[]
): InvoiceVerificationState | undefined => {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value ? "genuine" : "not_genuine";
    }

    if (typeof value !== "string") {
      continue;
    }

    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      continue;
    }

    if (
      includesAny(normalized, [
        "not genuine",
        "not_genuine",
        "invalid",
        "fake",
        "fraud",
        "tampered",
        "not found",
        "failed",
        "rejected",
        "suspicious",
      ])
    ) {
      return "not_genuine";
    }

    if (
      includesAny(normalized, [
        "genuine",
        "valid",
        "verified",
        "authentic",
        "approved",
      ])
    ) {
      return "genuine";
    }
  }

  return undefined;
};

const parseVerificationPayload = (
  payload: unknown,
  invoiceId: string,
  endpoint: string,
): InvoiceVerificationResult => {
  const envelope = toRecord(payload);
  const data = toRecord(envelope.data);
  const result = toRecord(data.result);
  const invoice = toRecord(data.invoice);
  const order = toRecord(invoice.order || data.order);

  const message = readString(
    data.message,
    result.message,
    envelope.message,
    invoice.message,
  );

  const hasVerifiedInvoiceRecord = Boolean(
    readString(
      data.id,
      data.invoiceNumber,
      data.invoice_number,
      data.orderNumber,
      data.order_number,
    ),
  );

  const state =
    (envelope.success === true && hasVerifiedInvoiceRecord
      ? "genuine"
      : undefined) ||
    parseVerificationState(
      data.isGenuine,
      data.genuine,
      data.isValid,
      data.valid,
      data.verified,
      data.authentic,
      data.verificationStatus,
      data.verificationState,
      result.isGenuine,
      result.genuine,
      result.isValid,
      result.valid,
      result.verified,
      result.verificationStatus,
      message,
    ) ||
    (envelope.success === false ? "not_genuine" : "unavailable");

  return {
    amount: readAmount(
      data.totalAmount,
      data.total_amount,
      data.amount,
      data.total,
      invoice.totalAmount,
      invoice.total_amount,
      invoice.total,
      order.totalAmount,
      order.total_amount,
      order.total,
    ),
    currency: readString(data.currency, invoice.currency, order.currency),
    endpoint,
    invoiceId,
    invoiceNumber: readString(
      data.invoiceNumber,
      data.invoice_number,
      invoice.invoiceNumber,
      invoice.invoice_number,
    ),
    message,
    orderNumber: readString(
      data.orderNumber,
      data.order_number,
      invoice.orderNumber,
      invoice.order_number,
      order.orderNumber,
      order.order_number,
    ),
    state,
  };
};

export const verifyInvoiceAuthenticity = async (
  invoiceId: string,
): Promise<InvoiceVerificationResult> => {
  const endpoint = getVerificationEndpoint(invoiceId);
  let lastErrorMessage = "Unable to verify this invoice right now.";

  try {
    const response = await axiosInstance.get<ApiEnvelope>(
      endpoint,
      REQUEST_CONFIG,
    );

    return parseVerificationPayload(response.data, invoiceId, endpoint);
  } catch (error) {
    const axiosError = error as AxiosError<ApiEnvelope>;
    const status = axiosError.response?.status;
    const payload = axiosError.response?.data;

    if (payload) {
      return parseVerificationPayload(payload, invoiceId, endpoint);
    }

    if (status === 404) {
      return {
        endpoint,
        invoiceId,
        messageKey: "invoice-verification.message.invoice-not-found",
        state: "not_genuine",
      };
    }

    lastErrorMessage = axiosError.message || lastErrorMessage;
  }

  return {
    endpoint,
    invoiceId,
    message:
      lastErrorMessage === "Unable to verify this invoice right now."
        ? undefined
        : lastErrorMessage,
    messageKey: "invoice-verification.message.unavailable",
    state: "unavailable",
  };
};
